// Phase 0b PR 9: contract activation gate tab.
//
// Reads the 15-item gate that CommitImport (PR 8) seeded and lets the
// user flip each item between Pending|Complete|Waived (Waived requires
// a reason). When every row is Complete or Waived, the Activate
// Contract CTA un-disables; on click the server-side rule re-confirms
// (Pangaea repo runs SERIALIZABLE + FOR UPDATE) and returns either the
// activated gate or a list of still-Pending item_keys.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { useActivateContract, useActivationGate, useUpdateActivationGateItem } from '../activationGateHooks';
const ITEM_STATES = [
    'Pending',
    'Complete',
    'Waived'
];
function stateBadgeVariant(state) {
    switch(state){
        case 'Complete':
            return 'success';
        case 'Waived':
            return 'neutral';
        case 'Activated':
            return 'success';
        default:
            return 'outline';
    }
}
export function ActivationGateTab({ contractProjectId }) {
    const { canEdit } = useProgramContext();
    const { data: gate, isLoading, error } = useActivationGate(contractProjectId);
    const update = useUpdateActivationGateItem();
    const activate = useActivateContract();
    // Track edits per item locally so the user can stage a Waived reason
    // before saving (the server requires reason on Waived).
    const [edits, setEdits] = useState({});
    // Last activate attempt's violations — surfaced inline so the user
    // sees which rows are still Pending without having to re-scan.
    const [lastViolations, setLastViolations] = useState(null);
    const summary = useMemo(()=>{
        if (!gate) return {
            complete: 0,
            waived: 0,
            pending: 0,
            total: 0
        };
        let complete = 0;
        let waived = 0;
        let pending = 0;
        for (const it of gate.items){
            if (it.state === 'Complete') complete++;
            else if (it.state === 'Waived') waived++;
            else pending++;
        }
        return {
            complete,
            waived,
            pending,
            total: gate.items.length
        };
    }, [
        gate
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-muted-foreground",
            children: "Loading activation gate…"
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "text-destructive",
            children: [
                "Failed to load activation gate: ",
                error instanceof Error ? error.message : 'Unknown error'
            ]
        });
    }
    if (!gate) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-muted-foreground",
            children: "No activation gate yet — the gate is seeded by Commit Import when the award comparison finalises."
        });
    }
    const handleSave = async (item)=>{
        if (!canEdit) return;
        const draft = edits[item.itemKey] ?? {
            state: item.state,
            reason: item.reason
        };
        if (draft.state === 'Waived' && !draft.reason.trim()) {
            toast.error('Reason is required when waiving an item');
            return;
        }
        try {
            await update.mutateAsync({
                contractProjectId,
                itemKey: item.itemKey,
                state: draft.state,
                reason: draft.reason
            });
            setEdits((prev)=>{
                const next = {
                    ...prev
                };
                delete next[item.itemKey];
                return next;
            });
        } catch  {
        // toast handled by hook
        }
    };
    const handleActivate = async ()=>{
        try {
            const resp = await activate.mutateAsync({
                contractProjectId
            });
            if (resp.violations && resp.violations.length > 0) {
                setLastViolations(resp.violations);
                toast.error(`Cannot activate — ${resp.violations.length} item(s) still need work`);
            } else {
                setLastViolations(null);
            }
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.activationGate.panel,
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                "data-testid": Testids.activationGate.header,
                "data-state": gate.state,
                children: [
                    /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                        className: "flex flex-row items-start justify-between gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraCardTitle, {
                                        className: "flex items-center gap-3",
                                        children: [
                                            "Activation Gate",
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: stateBadgeVariant(gate.state),
                                                children: gate.state
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-muted-foreground mt-1",
                                        "data-testid": Testids.activationGate.summary,
                                        children: [
                                            summary.complete,
                                            " complete · ",
                                            summary.waived,
                                            " waived · ",
                                            summary.pending,
                                            " pending ·",
                                            ' ',
                                            summary.total,
                                            " total"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: handleActivate,
                                disabled: !gate.readyToActivate || activate.isPending || gate.state !== 'Pending' || !canEdit,
                                "data-testid": Testids.activationGate.activateButton,
                                children: gate.state === 'Activated' ? 'Activated' : 'Activate Contract'
                            })
                        ]
                    }),
                    lastViolations && lastViolations.length > 0 && /*#__PURE__*/ _jsx(SpecraCardContent, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.activationGate.violationsList,
                            className: "rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "font-medium text-destructive",
                                    children: "Activation blocked — still pending:"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "mt-1 list-disc pl-5 text-destructive",
                                    children: lastViolations.map((key)=>/*#__PURE__*/ _jsx("li", {
                                            children: key
                                        }, key))
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                children: gate.items.map((item)=>{
                    const draft = edits[item.itemKey] ?? {
                        state: item.state,
                        reason: item.reason
                    };
                    const dirty = draft.state !== item.state || draft.state === 'Waived' && draft.reason !== item.reason;
                    const disabled = gate.state !== 'Pending';
                    return /*#__PURE__*/ _jsx(SpecraCard, {
                        "data-testid": Testids.activationGate.itemRow,
                        "data-item-key": item.itemKey,
                        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-medium",
                                            children: item.displayLabel || item.itemKey
                                        }),
                                        item.decidedBy && /*#__PURE__*/ _jsxs("div", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: [
                                                "Decided by ",
                                                item.decidedBy,
                                                item.decidedAt && ` · ${new Date(Number(item.decidedAt.seconds) * 1000).toLocaleString()}`
                                            ]
                                        }),
                                        item.reason && draft.state !== 'Waived' && /*#__PURE__*/ _jsxs("div", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: [
                                                "Reason: ",
                                                item.reason
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex w-full flex-col gap-2 sm:w-80",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: stateBadgeVariant(item.state),
                                                    className: "shrink-0",
                                                    children: item.state
                                                }),
                                                /*#__PURE__*/ _jsxs(Select, {
                                                    value: draft.state,
                                                    onValueChange: (next)=>setEdits((prev)=>({
                                                                ...prev,
                                                                [item.itemKey]: {
                                                                    state: next,
                                                                    reason: draft.reason
                                                                }
                                                            })),
                                                    disabled: disabled || !canEdit,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                                            className: "h-8",
                                                            "data-testid": Testids.activationGate.stateSelect,
                                                            "data-item-key": item.itemKey,
                                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                        }),
                                                        /*#__PURE__*/ _jsx(SelectContent, {
                                                            children: ITEM_STATES.map((s)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                    value: s,
                                                                    children: s
                                                                }, s))
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        draft.state === 'Waived' && /*#__PURE__*/ _jsx(SpecraTextarea, {
                                            value: draft.reason,
                                            onChange: (e)=>setEdits((prev)=>({
                                                        ...prev,
                                                        [item.itemKey]: {
                                                            state: draft.state,
                                                            reason: e.target.value
                                                        }
                                                    })),
                                            placeholder: "Reason for waiving this item (required)",
                                            rows: 2,
                                            "data-testid": Testids.activationGate.reasonInput,
                                            disabled: disabled || !canEdit
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            onClick: ()=>handleSave(item),
                                            disabled: !dirty || disabled || update.isPending || !canEdit,
                                            "data-testid": Testids.activationGate.saveItemButton,
                                            children: "Save"
                                        })
                                    ]
                                })
                            ]
                        })
                    }, item.itemKey);
                })
            })
        ]
    });
}
